import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LeadProvider } from "@/context/LeadContext";
import { captureUTM } from "@/lib/api";

import { PublicLayout } from "@/components/site/PublicLayout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import Packages from "@/pages/Packages";
import HowItWorks from "@/pages/HowItWorks";
import WhySudigi from "@/pages/WhySudigi";
import Resources from "@/pages/Resources";
import BlogPost from "@/pages/BlogPost";
import Contact from "@/pages/Contact";
import Checkout from "@/pages/Checkout";
import ThankYou from "@/pages/ThankYou";
import Track from "@/pages/Track";
import Legal from "@/pages/Legal";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import MarketplaceList from "@/pages/MarketplaceList";
import MarketplaceDetail from "@/pages/MarketplaceDetail";
import BuildMyBrand from "@/pages/BuildMyBrand";
import Community from "@/pages/Community";
import MarketingService from "@/pages/MarketingService";
import OrganiserDashboard from "@/pages/organiser/OrganiserDashboard";
import BrandProfileEdit from "@/pages/brand/BrandProfileEdit";
import BrandPublic from "@/pages/brand/BrandPublic";
import Messages from "@/pages/Messages";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeads from "@/pages/admin/AdminLeads";
import LeadDetail from "@/pages/admin/LeadDetail";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminServices from "@/pages/admin/AdminServices";
import AdminBdes from "@/pages/admin/AdminBdes";
import BdeDashboard from "@/pages/bde/BdeDashboard";
import CustomerDashboard from "@/pages/customer/CustomerDashboard";

const MK_KINDS = ["opportunities", "experts", "influencers", "podcasts", "events", "brands"];

function ScrollTop() {
    const { pathname } = useLocation();
    useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
    return null;
}

function Protected({ roles, children }) {
    const { user } = useAuth();
    if (user === null) return <div className="min-h-screen grid place-items-center bg-teal-900 text-cream">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;
    if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
    return children;
}

const pub = (el) => <PublicLayout>{el}</PublicLayout>;

function App() {
    useEffect(() => { captureUTM(); }, []);
    return (
        <AuthProvider>
            <BrowserRouter>
                <LeadProvider>
                    <ScrollTop />
                    <Toaster position="top-center" richColors />
                    <Routes>
                        <Route path="/" element={pub(<Home />)} />
                        <Route path="/services" element={pub(<Services />)} />
                        <Route path="/services/:slug" element={pub(<ServiceDetail />)} />
                        <Route path="/marketing/:slug" element={pub(<MarketingService />)} />
                        <Route path="/packages" element={pub(<Packages />)} />
                        <Route path="/how-it-works" element={pub(<HowItWorks />)} />
                        <Route path="/why-sudigi" element={pub(<WhySudigi />)} />
                        <Route path="/resources" element={pub(<Resources />)} />
                        <Route path="/resources/:slug" element={pub(<BlogPost />)} />
                        <Route path="/contact" element={pub(<Contact />)} />
                        <Route path="/legal/:kind" element={pub(<Legal />)} />
                        <Route path="/build-my-brand" element={pub(<BuildMyBrand />)} />
                        <Route path="/community" element={pub(<Community />)} />
                        <Route path="/brand/:slug" element={pub(<BrandPublic />)} />
                        <Route path="/messages" element={<Protected roles={["customer", "organiser", "bde", "admin", "super_admin"]}><Messages /></Protected>} />
                        <Route path="/organiser" element={<Protected roles={["organiser", "admin", "super_admin"]}><OrganiserDashboard /></Protected>} />
                        <Route path="/account/brand" element={<Protected roles={["customer", "organiser"]}><BrandProfileEdit /></Protected>} />

                        {MK_KINDS.map((k) => (
                            <Route key={k}>
                                <Route path={`/${k}`} element={pub(<MarketplaceList kind={k} />)} />
                                <Route path={`/${k}/:slug`} element={pub(<MarketplaceDetail kind={k} />)} />
                            </Route>
                        ))}

                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/thank-you/:orderId" element={<ThankYou />} />
                        <Route path="/track" element={pub(<Track />)} />
                        <Route path="/track/:orderId" element={pub(<Track />)} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />

                        <Route path="/admin" element={<Protected roles={["admin", "super_admin"]}><AdminDashboard /></Protected>} />
                        <Route path="/admin/leads" element={<Protected roles={["admin", "super_admin"]}><AdminLeads /></Protected>} />
                        <Route path="/admin/leads/:leadId" element={<Protected roles={["admin", "super_admin"]}><LeadDetail /></Protected>} />
                        <Route path="/admin/orders" element={<Protected roles={["admin", "super_admin"]}><AdminOrders /></Protected>} />
                        <Route path="/admin/services" element={<Protected roles={["admin", "super_admin"]}><AdminServices /></Protected>} />
                        <Route path="/admin/bdes" element={<Protected roles={["admin", "super_admin"]}><AdminBdes /></Protected>} />

                        <Route path="/bde" element={<Protected roles={["bde"]}><BdeDashboard /></Protected>} />
                        <Route path="/bde/leads/:leadId" element={<Protected roles={["bde"]}><LeadDetail /></Protected>} />

                        <Route path="/account" element={<Protected roles={["customer"]}><CustomerDashboard /></Protected>} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </LeadProvider>
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
